import React, {
  FunctionComponent,
  Reducer,
  useReducer,
  createContext,
  useEffect,
} from "react";
import {
  Action,
  ActionType,
  AppContextType,
  AppState,
  PharmacyType,
  PrescriptionDrug,
  UserLanguage,
  LowIncomeSubsidyStatus,
  PlanSNPType,
} from "../../@types";
import * as TypeGuards from "../../@types/guards";
import { saveBeneDrugs, saveBenePharmacies } from "../../api";
import { hasLoginSession } from "../../helpers/loginHelpers";
// Do not attempt to import from re-exports (../../helpers) at the moment
// This breaks tests
import { storageKey, cachedStore } from "../../helpers/storeHelpers";
import { logError } from "../../helpers";
import cookies from "browser-cookies";
import { deduplicatePrescriptions } from "../../helpers/beneficiaryInfoHelpers";
import { CoverageType } from "../../pages/QuestionRoutingPage/types";
import { useHistory } from "react-router";

export const defaultState: AppState = {
  beneficiary: undefined,
  compareList: [],
  county: undefined,
  csr: undefined,
  currentCoverage: undefined,
  cwCoverage: undefined,
  degradedIntegrations: [],
  demographicInfo: undefined,
  fips: undefined,
  futureLis: undefined,
  language: UserLanguage.ENGLISH,
  lis: undefined,
  mbpHandoffFailed: false,
  mbpReferrerUri: "",
  nextYearCoverage: undefined,
  prescriptions: [],
  pharmacies: [],
  pharmacyAddress: undefined,
  pharmacyType: PharmacyType.NONE,
  searchResultsFilters: {
    drugCoverageFilter: undefined,
    insuranceCarrierFilter: "",
    insuranceCarriers: [],
    planCategoriesFilter: [],
    planCoveragesFilter: [],
    sortBy: undefined,
    starRatingFilter: "",
  },
  snpFilters: [],
  tealiumLoaded: false,
  viewMedigap: false,
  zipcode: undefined,
};

const initialState: AppState = cachedStore || defaultState;

initialState.language = navigator.language.split(/[-_]/)[0] as UserLanguage;

const resetState = (): AppState => {
  // Clear session storage relative to domain
  window.sessionStorage.clear();

  // IE 11 shares session ID cookie over multiple windows and tabs, unlike other browsers
  cookies.erase("sid");

  return { ...defaultState };
};

const updatePrescriptions = async (
  prescriptions: PrescriptionDrug[]
): Promise<void> => {
  if (hasLoginSession()) {
    try {
      saveBeneDrugs(prescriptions);
    } catch (e) {
      logError(
        "Failed to save beneficiary drugs (AppStore/updatePrescriptions)",
        e
      );
    }
  }
};

const setPrescriptions = (state: AppState, { payload }: Action): AppState => {
  let prescriptions = TypeGuards.hasPrescriptions(payload)
    ? payload
    : state.prescriptions;

  prescriptions = deduplicatePrescriptions(prescriptions);
  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return { ...state, prescriptions };
};

const addPrescription = (
  state: AppState,
  { payload: prescription }: Action
): AppState => {
  const prescriptions = TypeGuards.isPrescription(prescription)
    ? [...state.prescriptions, prescription]
    : state.prescriptions;

  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return {
    ...state,
    prescriptions,
  };
};

const editPrescription = (state: AppState, { payload }: Action): AppState => {
  if (TypeGuards.hasIndex(payload) && TypeGuards.hasDrug(payload)) {
    const { index, drug } = payload;

    const prescriptions = state.prescriptions.map((prescription, i) => {
      if (i !== index) {
        return prescription;
      }

      return {
        ...prescription,
        ...drug,
      };
    });

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

const deletePrescription = (state: AppState, { payload }: Action): AppState => {
  if (TypeGuards.hasIndex(payload)) {
    const prescriptions = state.prescriptions.filter(
      (p, i) => i !== payload.index
    );

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

export const hasMailOrderPharmacy = (state: AppState): boolean => {
  return [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
    state.pharmacyType
  );
};

const addPharmacy = (
  state: AppState,
  { payload: pharmacy }: Action
): AppState => {
  if (TypeGuards.isPharmacy(pharmacy)) {
    const pharmacies = [...state.pharmacies, pharmacy];
    return { ...state, pharmacies };
  }

  return state;
};

const deletePharmacy = (
  state: AppState,
  { payload: npi }: Action
): AppState => {
  if (typeof npi === "string") {
    const pharmacies = state.pharmacies.filter(p => p.npi !== npi);
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

const savePharmacies = (state: AppState): AppState => {
  if (hasLoginSession()) {
    const npis = state.pharmacies.map(p => p.npi);
    if (hasMailOrderPharmacy(state)) {
      npis.push("mail-order");
    }
    try {
      saveBenePharmacies(npis);
    } catch (e) {
      logError(
        "Failed to save beneficiary pharmacies (AppStore/savePharmacies)",
        e
      );
    }
  }
  return state;
};

const setPharmacies = (state: AppState, { payload }: Action): AppState => {
  const pharmacies = TypeGuards.hasPharmacies(payload)
    ? payload
    : state.pharmacies;

  return { ...state, pharmacies };
};

const updateLis = (
  state: AppState,
  { payload: lis }: Action,
  setFutureLis = false
): AppState => {
  if (!TypeGuards.isLIS(lis)) {
    return state;
  }

  const snpFilters = state.snpFilters || [];

  if (
    [
      LowIncomeSubsidyStatus.LIS_LEVEL_1A,
      LowIncomeSubsidyStatus.LIS_LEVEL_2,
      LowIncomeSubsidyStatus.LIS_LEVEL_3,
    ].includes(lis)
  ) {
    if (!Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_NOT_SNP)) {
      snpFilters.push(PlanSNPType.SNP_TYPE_NOT_SNP);
    }

    if (
      !Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE)
    ) {
      snpFilters.push(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE);
    }
  }

  return setFutureLis
    ? { ...state, futureLis: lis, snpFilters }
    : { ...state, lis, snpFilters };
};

const updatePlanType = (state: AppState, { payload }: Action): AppState => {
  const planType = TypeGuards.isPlanType(payload) ? payload : undefined;

  return {
    ...state,
    planType,
    searchResultsFilters: defaultState.searchResultsFilters,
  };
};

/**
 * Updates the plan-specific network status for a pharmacy. Called whenever
 * `PharmacyNetworkStatus` is used, to prevent flash-of-incorrect-state issues
 * when switching between plans
 */
const updatePharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.isPharmacyInfo(payload)) {
    const pharmacies = state.pharmacies.map(p => {
      return p.npi === payload.npi
        ? { ...p, in_network: payload.in_network, preferred: payload.preferred }
        : p;
    });
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

const updateSearchResultsFiltersCarriers = (
  state: AppState,
  { payload }: Action
): AppState => {
  const insuranceCarriers = TypeGuards.hasInsuranceCarriers(payload)
    ? payload
    : [];

  return {
    ...state,
    searchResultsFilters: {
      ...state.searchResultsFilters,
      insuranceCarriers,
    },
  };
};

const reducer: Reducer<AppState, Action> = (state, action) => {
  const { payload } = action;

  function newStateIf(
    method: keyof typeof TypeGuards,
    property: keyof AppState
  ): AppState {
    return TypeGuards[method](payload)
      ? { ...state, [property]: payload }
      : state;
  }

  switch (action.type) {
    case ActionType.ADD_BENEFICIARY:
      return newStateIf("isBeneficiary", "beneficiary");
    case ActionType.ADD_CURRENT_COVERAGE:
      return newStateIf("isSearchResultPlan", "currentCoverage");
    case ActionType.ADD_NEXT_YEAR_COVERAGE:
      return newStateIf("isSearchResultPlan", "nextYearCoverage");
    case ActionType.ADD_CSR:
      return newStateIf("isCSR", "csr");
    case ActionType.ADD_PHARMACY:
      return addPharmacy(state, action);
    case ActionType.ADD_PRESCRIPTION:
      return addPrescription(state, action);
    case ActionType.DELETE_PHARMACY:
      return deletePharmacy(state, action);
    case ActionType.DELETE_PRESCRIPTION:
      return deletePrescription(state, action);
    case ActionType.EDIT_PRESCRIPTION:
      return editPrescription(state, action);
    case ActionType.RESET_PHARMACIES:
      return { ...state, pharmacies: [] };
    case ActionType.RESET_STATE:
      return { ...state, ...resetState(), language: state.language };
    case ActionType.SAVE_PHARMACIES:
      return savePharmacies(state);
    case ActionType.SET_PRESCRIPTIONS:
      return setPrescriptions(state, action);
    case ActionType.SET_PHARMACIES: {
      return setPharmacies(state, action);
    }
    case ActionType.SET_TEALIUM_LOADED:
      return { ...state, tealiumLoaded: payload ? true : false };
    case ActionType.UPDATE_COUNTY:
      return newStateIf("isCounty", "county");
    case ActionType.UPDATE_COMPARE_LIST:
      return newStateIf("hasSearchResultPlans", "compareList");
    case ActionType.UPDATE_FIPS:
      return newStateIf("hasFiveDigitString", "fips");
    case ActionType.UPDATE_LIS:
      return updateLis(state, action);
    case ActionType.UPDATE_FUTURE_LIS:
      return updateLis(state, action, true);
    case ActionType.UPDATE_PHARMACY_TYPE:
      return newStateIf("isPharmacyType", "pharmacyType");
    case ActionType.UPDATE_PLANTYPE:
      return updatePlanType(state, action);
    case ActionType.UPDATE_MBP_HANDOFF_FAILED:
      return newStateIf("isBoolean", "mbpHandoffFailed");
    case ActionType.UPDATE_ZIPCODE:
      return newStateIf("hasFiveDigitString", "zipcode");
    case ActionType.UPDATE_VIEW_MEDIGAP:
      return newStateIf("isBoolean", "viewMedigap");
    case ActionType.UPDATE_DEGRADED_INTEGRATIONS:
      return newStateIf("hasStrings", "degradedIntegrations");
    case ActionType.UPDATE_YEAR:
      return newStateIf("hasYear", "year");
    case ActionType.UPDATE_LANGUAGE:
      return newStateIf("isLanguage", "language");
    case ActionType.UPDATE_SELECTED_PLAN:
      return newStateIf("isPlanOrSRP", "selectedPlan");
    case ActionType.UPDATE_LAST_ACTIVITY:
      return newStateIf("isDate", "lastActivity");
    case ActionType.UPDATE_SNP_FILTERS:
      return newStateIf("hasPlanSNPTypes", "snpFilters");
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS:
      return newStateIf("hasSearchResultsFilters", "searchResultsFilters");
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS:
      return updateSearchResultsFiltersCarriers(state, action);
    case ActionType.UPDATE_PHARMACY_ADDRESS:
      return { ...state, pharmacyAddress: payload as string };
    case ActionType.UPDATE_PHARMACY_NETWORK_STATUS:
      return updatePharmacyNetworkStatus(state, action);
    case ActionType.UPDATE_DEMOGRAPHICS:
      return newStateIf("isDemographicInfo", "demographicInfo");
    case ActionType.UPDATE_CW_COVERAGE:
      return { ...state, cwCoverage: payload as CoverageType };
    case ActionType.UPDATE_MBP_REFERRER_URI:
      return newStateIf("isString", "mbpReferrerUri");
    default:
      throw new Error(`Reducer missing handler for:
ACTION: ${action.type}
STATE: ${JSON.stringify(state, null, 2)}`);
  }
};

export const AppContext = createContext({} as AppContextType);

export const AppStoreProvider: FunctionComponent<{
  initialState?: AppState;
}> = props => {
  const [state, dispatch] = useReducer(
    reducer,
    props.initialState || { ...initialState, tealiumLoaded: false }
  );
  const history = useHistory();

  useEffect(() => {
    const { beneficiary } = state;

    // Every time the state changes, store it in sessionStorage
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(
        beneficiary
          ? {
              ...state,
              beneficiary: { ...beneficiary, ssn: "", hicn: "", mbi: "" },
            }
          : state
      )
    );
  }, [state]);

  // For the purposes of testing global session management, we need to be able to
  // set app state within integration tests
  if (window.Cypress) {
    window.store = { state, dispatch };
    window.router = { history };
  }

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  );
};

export default {
  AppContext,
  AppStoreProvider,
};
